/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, type TableProps } from 'antd';
import { MonthNavigation } from 'components';
import { useGetSalesSettlements, useMeInfo } from 'hooks';
import { setPassCertification } from 'utils';

import {
  WhiteWrapper,
  SearchFormWrapper,
  TableWrapper,
  StatusChip,
  MonthQuickFilterButton,
  MonthQuickFilterWrapper
} from './styles';

interface TSettlement {
  settlementIdx: number;
  settlementStartDate: string;
  settlementEndDate: string;
  settlementName: string;
  bizName: string;
  bizNumber: string;
  bizSubNumber: string;
  bizPresidentName: string;
  accountBizEmail: string;
  linkedPartner: string;
  salesAmount: number;
  settlementAmount: number;
  status: 'READY' | 'CONFIRMED' | 'EVIDENCE_COMPLETED' | 'COMPLETED';
  evidenceStatus: 'REQUESTED' | 'READY' | 'COMPLETED' | 'FAILED';
  depositStatus: 'NONE' | 'READY' | 'COMPLETED';
}

const columns: TableProps<TSettlement>['columns'] = [
  { title: '정산월', dataIndex: 'settlementName', align: 'center', width: '60px' },
  {
    title: '시작일',
    dataIndex: 'settlementStartDate',
    align: 'center',
    width: '60px',
    render: (value) => moment(value).format('MM/DD')
  },
  {
    title: '종료일',
    dataIndex: 'settlementEndDate',
    align: 'center',
    width: '60px',
    render: (value) => moment(value).format('MM/DD')
  },
  { title: '사업자명', dataIndex: 'bizName', align: 'center' },
  { title: '사업자번호', dataIndex: 'bizNumber', align: 'center', width: '110px' },
  { title: '대표자명', dataIndex: 'bizPresidentName', align: 'center', width: '90px' },
  { title: '이메일', dataIndex: 'accountBizEmail', align: 'center', width: '180px', render: (value) => value || '-' },
  { title: '제휴점명', dataIndex: 'linkedPartner', align: 'center' },
  {
    title: '매출금액',
    dataIndex: 'salesAmount',
    align: 'right',
    width: '100px',
    render: (value) => (typeof value === 'number' ? Number(value).toLocaleString() : '-')
  },
  {
    title: '수수료',
    dataIndex: 'feeAmount',
    align: 'right',
    width: '100px',
    render: (value) => (typeof value === 'number' ? Number(value).toLocaleString() : '-')
  },
  {
    title: '정산(예정)금액',
    dataIndex: 'settlementAmount',
    align: 'right',
    width: '110px',
    render: (value) => (typeof value === 'number' ? Number(value).toLocaleString() : '-')
  },
  {
    title: '정산 확정',
    align: 'center',
    width: '100px',
    render: (_, values) => {
      if (values?.status === 'READY') return <StatusChip label="대기" />;
      return <StatusChip label="완료" isActive={false} />;
    }
  },
  {
    title: '증빙 처리',
    align: 'center',
    width: '100px',
    render: (_, values) => {
      if (values?.status !== 'READY' && values?.settlementAmount === 0) return <StatusChip label="완료" isActive={false} />;
      if (values?.status !== 'READY' && !values?.evidenceStatus) return '-';
      if (values?.status !== 'READY' && values?.evidenceStatus === 'READY') return <StatusChip label="발급 요청" isActive />;
      if (values?.status !== 'READY' && values?.evidenceStatus === 'REQUESTED')
        return <StatusChip label="발급 진행중" style={{ background: '#ffbe00', color: '#fff' }} />;
      if (values?.status !== 'READY' && values?.evidenceStatus === 'FAILED')
        return <StatusChip label="발급 실패" isActive style={{ background: 'red' }} />;
      if (values?.status !== 'READY' && values?.evidenceStatus === 'COMPLETED')
        return <StatusChip label="발급 완료" isActive={false} />;
      return '-';
    }
  },
  {
    title: '입금 처리',
    align: 'center',
    width: '100px',
    render: (_, values) => {
      if (values?.status !== 'READY' && values?.settlementAmount === 0) return <StatusChip label="완료" isActive={false} />;
      if (values?.status !== 'READY' && values?.depositStatus === 'READY') return <StatusChip label="대기" />;
      if (values?.status !== 'READY' && values?.depositStatus === 'COMPLETED')
        return <StatusChip label="완료" isActive={false} />;
      return '-';
    }
  }
];

export const getInitialSettlementDate = (year?: string | null, month?: string | null) => {
  const targetYear = Number(year) || moment().year();
  const targetMonth = Number(month) || new Date().getMonth() + 1;

  return new Date(targetYear, targetMonth - 1, 1);
};

export const getSettlementSearchParams = (date: Date, month: number | null = date.getMonth() + 1) => ({
  year: date.getFullYear(),
  month
});

export const getMonthQuickFilterOptions = (year: number, maxYear: number, maxMonth: number) => [
  { label: '전체', value: null, disabled: false },
  ...Array.from({ length: 12 }).map((_, index) => {
    const month = index + 1;
    const disabled = year === maxYear && month > maxMonth;

    return { label: `${month}월`, value: month, disabled };
  })
];

export const getLatestSettlementMonth = (settlements: Array<{ settlementName?: string }> = []) => {
  const months = settlements
    .map((settlement) => settlement.settlementName?.match(/^(\d{1,2})월$/)?.[1])
    .filter(Boolean)
    .map((month) => Number(month))
    .filter((month) => month >= 1 && month <= 12);

  if (!months.length) {
    return null;
  }

  return Math.max(...months);
};

export const hasSettlementMonth = (month: number, settlements?: Array<{ settlementName?: string }>) =>
  (settlements || []).some((settlement) => settlement.settlementName === `${month}월`);

export const getMonthButtonDisabled = (
  month: number,
  year: number,
  maxYear: number,
  maxMonth: number,
  settlements?: Array<{ settlementName?: string }>
) => {
  if (year === maxYear && month > maxMonth) {
    return true;
  }

  return !hasSettlementMonth(month, settlements);
};

const SalesSettlement = () => {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const { meInfo } = useMeInfo();

  const defaultYear = urlParams.get('year');
  const defaultMonth = urlParams.get('month');
  const maxYear = moment().year();
  const maxMonth = moment().month() + 1;
  const initialDate = getInitialSettlementDate(defaultYear, defaultMonth);

  const [selectedMonth, setSelectedMonth] = useState<Date>(() => initialDate);
  const [selectedQuickMonth, setSelectedQuickMonth] = useState<number | null>(() => (defaultMonth ? Number(defaultMonth) : null));
  const [searchParams, setSearchParams] = useState(() =>
    getSettlementSearchParams(initialDate, defaultMonth ? Number(defaultMonth) : null)
  );
  const { data } = useGetSalesSettlements(searchParams);
  const { data: allYearData } = useGetSalesSettlements({ year: searchParams.year, month: null });
  const allYearSettlements = useMemo(() => allYearData?.content || [], [allYearData]);
  const latestSettlementMonth = getLatestSettlementMonth(allYearSettlements);
  const navigationMaxMonth = latestSettlementMonth || maxMonth;

  useEffect(() => {
    if (!meInfo?.admin?.isCertification) {
      setPassCertification(meInfo?.admin?.id, false);
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedQuickMonth === null) {
      return;
    }

    if (!allYearData) {
      return;
    }

    if (hasSettlementMonth(selectedQuickMonth, allYearSettlements)) {
      return;
    }

    const nextSearchParams = getSettlementSearchParams(selectedMonth, null);
    urlParams.set('year', String(nextSearchParams.year));
    urlParams.delete('month');
    setUrlParams(urlParams);
    setSelectedQuickMonth(null);
    setSearchParams(nextSearchParams);
  }, [allYearData, allYearSettlements, selectedMonth, selectedQuickMonth, setUrlParams, urlParams]);

  const handleMonthChange = (date: Date) => {
    const nextMonth = selectedQuickMonth;
    const nextSearchParams = getSettlementSearchParams(date, nextMonth);
    urlParams.set('year', String(nextSearchParams.year));

    if (nextSearchParams.month) {
      urlParams.set('month', String(nextSearchParams.month));
    } else {
      urlParams.delete('month');
    }

    setUrlParams(urlParams);
    setSelectedMonth(date);
    setSelectedQuickMonth(nextSearchParams.month);
    setSearchParams(nextSearchParams);
  };

  const handleQuickMonthChange = (month: number | null) => {
    const nextDate = new Date(selectedMonth);
    const targetMonth = month || nextDate.getMonth() + 1;

    nextDate.setMonth(targetMonth - 1);

    const nextSearchParams = getSettlementSearchParams(nextDate, month);
    urlParams.set('year', String(nextSearchParams.year));

    if (month) {
      urlParams.set('month', String(month));
    } else {
      urlParams.delete('month');
    }

    setUrlParams(urlParams);
    setSelectedMonth(nextDate);
    setSelectedQuickMonth(month);
    setSearchParams(nextSearchParams);
  };

  const quickFilterOptions = getMonthQuickFilterOptions(selectedMonth.getFullYear(), maxYear, navigationMaxMonth).map(
    (option) => {
      if (option.value === null) {
        return option;
      }

      return {
        ...option,
        disabled: getMonthButtonDisabled(
          option.value,
          selectedMonth.getFullYear(),
          maxYear,
          navigationMaxMonth,
          allYearSettlements
        )
      };
    }
  );

  const handleRowClick = (values: TSettlement) => {
    navigate(`/salesSettlement/${values.settlementIdx}`);
  };

  return (
    <div>
      <SearchFormWrapper>
        <MonthNavigation date={selectedMonth} onChange={handleMonthChange} maxYear={maxYear} />
        <MonthQuickFilterWrapper>
          {quickFilterOptions.map((option) => (
            <MonthQuickFilterButton
              key={option.label}
              type="button"
              $isActive={selectedQuickMonth === option.value}
              disabled={option.disabled}
              onClick={() => handleQuickMonthChange(option.value)}
            >
              {option.label}
            </MonthQuickFilterButton>
          ))}
        </MonthQuickFilterWrapper>
      </SearchFormWrapper>

      <WhiteWrapper>
        <div style={{ padding: '16px', marginBottom: '8px', borderRadius: '4px', background: '#eeeeee' }}>
          <div>※ 매출 금액은 매일 1회 업데이트 됩니다. 정산에 이상이 있는 경우 1:1문의를 이용해주세요.</div>
          <div>※ 고객사와 직접거래가 있는 경우 정산금액에서 제외되어 노출됩니다.</div>
          <div>※ 그룹 정산(프랜차이즈)으로 진행되는 경우 정산서가 노출되지 않습니다.</div>
        </div>
        <TableWrapper>
          <Table
            columns={columns}
            dataSource={data?.content || []}
            pagination={false}
            onRow={(values) => ({ onClick: () => handleRowClick(values) })}
          />
        </TableWrapper>
      </WhiteWrapper>
    </div>
  );
};

export default SalesSettlement;
